// src/pages/PagesUtilisateurs/DashboardUsers/hooks/useDashboardCounts.js

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import { getNotificationsByUser } from "../../../../services/notificationServices";
import { getMessagesByUser } from "../../../../services/messageServices";
import { getAllMessagesContactForm } from "../../../../services/contactformServices";

import { getUserFavorites } from "../../../../services/usersTechniquesServices";
import { getUsersEvents } from "../../../../services/usersEventsServices";

import { getReglementsByUser } from "../../../../services/achatsServices";

import { getAdherentsByUser } from "../../../../services/adherentServices";
import { getObjectifsByAdherent } from "../../../../services/objectifsServices";

import {
  getEventId,
  getPendingPayments,
  getTechniqueId,
  isReadFlag,
  uniqueIds,
} from "../utils/dashboardUser.utils";

const INITIAL_COUNTS = {
  payments: {
    total: 0,
  },

  notifications: {
    total: 0,
    unread: 0,
  },

  messages: {
    total: 0,
    unread: 0,
  },

  contact: {
    total: 0,
    unread: 0,
  },

  objectifs: {
    total: 0,
  },

  techniques: {
    total: 0,
  },

  events: {
    total: 0,
  },
};

export default function useDashboardCounts({
  userId,
  isAdminOrTeacher,
}) {
  const [counts, setCounts] =
    useState(INITIAL_COUNTS);

  const [loading, setLoading] = useState(false);

  const updateCount = useCallback((key, values) => {
    setCounts((previous) => ({
      ...previous,

      [key]: {
        ...(previous[key] || {}),
        ...(values || {}),
      },
    }));
  }, []);

  const loadCounts = useCallback(async () => {
    if (!userId) return;

    setLoading(true);

    try {
      const [
        notifications,
        messages,
        contactMessages,
        favoriteTechniques,
        favoriteEvents,
        payments,
        adherents,
      ] = await Promise.all([
        getNotificationsByUser(userId).catch(() => []),

        getMessagesByUser(userId).catch(() => []),

        isAdminOrTeacher
          ? getAllMessagesContactForm(userId).catch(
              () => [],
            )
          : Promise.resolve([]),

        getUserFavorites(userId).catch(() => []),

        getUsersEvents(userId).catch(() => []),

        getReglementsByUser(userId).catch(() => []),

        getAdherentsByUser(userId).catch(() => []),
      ]);

      /* Notifications */

      const notificationsList = Array.isArray(
        notifications,
      )
        ? notifications
        : [];

      const notificationsUnread =
        notificationsList.filter(
          (notification) =>
            !isReadFlag(
              notification.read_notification,
            ),
        ).length;

      /* Messages */

      const messagesList = (
        Array.isArray(messages) ? messages : []
      ).filter((message) => !message.is_copy);

      const messagesUnread = messagesList.filter(
        (message) =>
          !isReadFlag(message.read_message),
      ).length;

      /* Contact */

      const contactList = Array.isArray(
        contactMessages,
      )
        ? contactMessages
        : [];

      const contactUnread = contactList.filter(
        (message) =>
          !isReadFlag(message.read_message),
      ).length;

      /* Techniques */

      const techniqueIds = uniqueIds(
        (Array.isArray(favoriteTechniques)
          ? favoriteTechniques
          : []
        ).map(getTechniqueId),
      );

      /* Events */

      const eventIds = uniqueIds(
        (Array.isArray(favoriteEvents)
          ? favoriteEvents
          : []
        ).map(getEventId),
      );

      /* Paiements */

      const pendingPayments =
        getPendingPayments(payments);

      /* Objectifs */

      const adherentsList = Array.isArray(
        adherents,
      )
        ? adherents
        : [];

      const objectifsResults =
        await Promise.all(
          adherentsList.map((adherent) =>
            getObjectifsByAdherent(
              adherent.id,
            ).catch(() => []),
          ),
        );

      const objectifsCount =
        objectifsResults.reduce(
          (total, objectifs) =>
            total +
            (Array.isArray(objectifs)
              ? objectifs.filter(
                  (objectif) =>
                    !objectif.is_done,
                ).length
              : 0),
          0,
        );

      setCounts({
        payments: {
          total: pendingPayments.length,
        },

        notifications: {
          total: notificationsList.length,
          unread: notificationsUnread,
        },

        messages: {
          total: messagesList.length,
          unread: messagesUnread,
        },

        contact: {
          total: contactList.length,
          unread: contactUnread,
        },

        objectifs: {
          total: objectifsCount,
        },

        techniques: {
          total: techniqueIds.length,
        },

        events: {
          total: eventIds.length,
        },
      });
    } catch (error) {
      console.error(
        "Erreur chargement compteurs dashboard :",
        error,
      );
    } finally {
      setLoading(false);
    }
  }, [userId, isAdminOrTeacher]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  return {
    counts,
    loading,

    updateCount,
    reloadCounts: loadCounts,
  };
}